import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { translations } from "./translations";

const LanguageContext = createContext(null);

const SUPPORTED = ["fr", "de", "en"];
const STORAGE_KEY = "ct3f-lang";

const META = {
  fr: {
    title: "CT3F — Contrôle Technique des trois Frontières · Luxembourg · France · Belgique",
    description:
      "Passage au contrôle technique, prestations à l'immatriculation, rapatriement et import-export entre LU, FR et BE. Particuliers et professionnels. Contrôle réalisé par une personne agréée.",
    htmlLang: "fr",
    ogLocale: "fr_FR",
  },
  de: {
    title: "CT3F — Technische Prüfung im Dreiländereck · Luxemburg · Frankreich · Belgien",
    description:
      "Technische Fahrzeugprüfung, Zulassung, Überführung sowie Import-/Exportverfahren zwischen LU, FR und BE. Privat und gewerblich. Durchführung durch eine anerkannte Fachkraft.",
    htmlLang: "de",
    ogLocale: "de_DE",
  },
  en: {
    title: "CT3F — Technical Inspection across three borders · Luxembourg · France · Belgium",
    description:
      "Vehicle technical inspection, registration, transfer and import/export between LU, FR and BE. Private and professional clients. Carried out by an approved professional.",
    htmlLang: "en",
    ogLocale: "en_GB",
  },
};

const setMeta = (selector, attr, value) => {
  const el = document.querySelector(selector);
  if (el && value != null) el.setAttribute(attr, value);
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "fr";
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return SUPPORTED.includes(saved) ? saved : "fr";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, lang);
    const meta = META[lang] || META.fr;
    document.documentElement.lang = meta.htmlLang;
    document.title = meta.title;
    setMeta('meta[name="description"]', "content", meta.description);
    setMeta('meta[property="og:locale"]', "content", meta.ogLocale);
    setMeta('meta[property="og:title"]', "content", meta.title);
    setMeta('meta[property="og:description"]', "content", meta.description);
    setMeta('meta[name="twitter:title"]', "content", meta.title);
    setMeta('meta[name="twitter:description"]', "content", meta.description);
  }, [lang]);

  const value = useMemo(
    () => ({
      lang,
      setLang,
      t: translations[lang],
      supported: SUPPORTED,
    }),
    [lang]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useI18n = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useI18n must be used within LanguageProvider");
  return ctx;
};
